import {
  ArrowPathIcon,
  ChevronRightIcon,
  PhoneIcon,
} from '@heroicons/react/24/outline';
import React from 'react';

import { useGetTaskRunsQuery } from '@/services/api/blandService';
import { Task } from '@/services/types/client-intake-types';
import {
  EnhancedBlandWorkflowState,
  EnhancedBlandWorkflowStatus,
} from '@/types/enhanced-bland-call';

interface VoiceAgentSummaryCardProps {
  task: Task;
  workflow: EnhancedBlandWorkflowState | null;
  isLoading?: boolean;
  onStart: () => void;
  onRetry: () => void;
  onRefresh: () => void;
  onViewDetails: () => void;
  retryLoading?: boolean;
  refreshLoading?: boolean;
}

export const VoiceAgentSummaryCard: React.FC<VoiceAgentSummaryCardProps> = ({
  task,
  workflow,
  isLoading = false,
  onStart,
  onRetry,
  onRefresh,
  onViewDetails,
  retryLoading = false,
  refreshLoading = false,
}) => {
  // Fetch run history count for indicator
  const { data: runsData } = useGetTaskRunsQuery(
    { matterId: task.matter, taskId: task.id },
    { skip: isLoading },
  );

  if (isLoading) {
    return (
      <div className="bg-gray-50 border border-gray-200 rounded-md p-2.5">
        <div className="animate-pulse flex items-center gap-2">
          <div className="h-6 w-6 bg-gray-300 rounded" />
          <div className="flex-1">
            <div className="h-3 bg-gray-300 rounded w-24" />
          </div>
        </div>
      </div>
    );
  }

  // No workflow – show start state
  if (!workflow) {
    return (
      <div className="bg-white border border-gray-200 rounded-md p-2.5">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <PhoneIcon className="h-4 w-4 text-gray-500 flex-shrink-0" />
            <span className="text-sm font-medium text-gray-700 truncate">
              AI Voice Agent
              {runsData && runsData.count > 0 && (
                <span className="ml-1.5 text-xs font-normal text-gray-400">
                  ({runsData.count} runs)
                </span>
              )}
            </span>
          </div>
          <button
            type="button"
            onClick={onStart}
            disabled={isLoading}
            className="flex-shrink-0 px-3 py-1 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 rounded disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Start
          </button>
        </div>
      </div>
    );
  }

  // Get current status from most recent event or fallback
  const getCurrentStatus = () => {
    const recentEvent =
      workflow.event_log?.[workflow.event_log.length - 1]?.description;

    if (workflow.status === EnhancedBlandWorkflowStatus.FAILED_READY_CHECK) {
      return workflow.readiness?.summary || 'Missing required information';
    }

    return recentEvent || 'Active';
  };

  const needsAttention = false; // Removed retry functionality - FAILED_READY_CHECK is now terminal
  const isActive = [
    EnhancedBlandWorkflowStatus.STARTED,
    EnhancedBlandWorkflowStatus.THINKING,
    EnhancedBlandWorkflowStatus.CALL_IN_PROGRESS,
    EnhancedBlandWorkflowStatus.WAITING,
  ].includes(workflow.status);
  const isCompleted = workflow.status === EnhancedBlandWorkflowStatus.COMPLETED;
  const isFailed = [
    EnhancedBlandWorkflowStatus.FAILED,
    EnhancedBlandWorkflowStatus.FAILED_INELIGIBLE,
    EnhancedBlandWorkflowStatus.FAILED_READY_CHECK, // Now treated as failed/terminal
  ].includes(workflow.status);

  // Status styling with indicators
  const getStatusConfig = () => {
    switch (workflow.status) {
      case EnhancedBlandWorkflowStatus.CALL_IN_PROGRESS:
        return {
          bg: 'bg-white border-blue-200',
          dotColor: 'bg-blue-500',
          textColor: 'text-blue-600',
          animate: true,
        };
      case EnhancedBlandWorkflowStatus.THINKING:
      case EnhancedBlandWorkflowStatus.STARTED:
        return {
          bg: 'bg-white border-blue-200',
          dotColor: 'bg-blue-500',
          textColor: 'text-blue-600',
          animate: true,
        };
      case EnhancedBlandWorkflowStatus.WAITING:
        return {
          bg: 'bg-white border-gray-200',
          dotColor: 'bg-gray-400',
          textColor: 'text-gray-500',
          animate: false,
        };
      case EnhancedBlandWorkflowStatus.FAILED_READY_CHECK:
        return {
          bg: 'bg-white border-amber-200',
          dotColor: 'bg-amber-500',
          textColor: 'text-amber-600',
          animate: false,
        };
      case EnhancedBlandWorkflowStatus.COMPLETED:
        return {
          bg: 'bg-white border-green-200',
          dotColor: 'bg-green-500',
          textColor: 'text-green-600',
          animate: false,
        };
      case EnhancedBlandWorkflowStatus.FAILED:
      case EnhancedBlandWorkflowStatus.FAILED_INELIGIBLE:
        return {
          bg: 'bg-white border-red-200',
          dotColor: 'bg-red-500',
          textColor: 'text-red-600',
          animate: false,
        };
      default:
        return {
          bg: 'bg-white border-gray-200',
          dotColor: 'bg-gray-400',
          textColor: 'text-gray-500',
          animate: false,
        };
    }
  };

  const statusConfig = getStatusConfig();
  const currentStatus = getCurrentStatus();
  const callCount = workflow.call_results?.length || 0;

  return (
    <button
      type="button"
      onClick={onViewDetails}
      className={`w-full text-left border rounded-md p-2.5 hover:bg-gray-50 transition-colors ${statusConfig.bg}`}
    >
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <div className="relative flex-shrink-0">
            <PhoneIcon className="h-4 w-4 text-gray-500" />
            <span
              className={`absolute -top-0.5 -right-0.5 h-2 w-2 rounded-full ${statusConfig.dotColor} ${statusConfig.animate ? 'animate-pulse' : ''}`}
            />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5">
              <span className="text-sm font-medium text-gray-700">
                AI Voice Agent
              </span>
              {callCount > 0 && (
                <span className="text-xs text-gray-400">
                  {callCount}/{workflow.max_calls_allowed}
                </span>
              )}
            </div>
            <div className={`text-xs truncate ${statusConfig.textColor}`}>
              {currentStatus}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-1.5 flex-shrink-0">
          {(isFailed || isCompleted) && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onStart();
              }}
              disabled={isLoading}
              className="px-2 py-0.5 text-xs font-medium text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded disabled:opacity-50 transition-colors"
            >
              {isLoading ? '...' : 'Restart'}
            </button>
          )}
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onRefresh();
            }}
            disabled={refreshLoading}
            className="p-1 text-gray-400 hover:text-gray-600 rounded transition-colors"
            title="Refresh"
          >
            <ArrowPathIcon
              className={`h-3.5 w-3.5 ${refreshLoading ? 'animate-spin' : ''}`}
            />
          </button>
          <ChevronRightIcon className="h-3.5 w-3.5 text-gray-400" />
        </div>
      </div>
    </button>
  );
};
